/**
 * news.js — client half of the What's New feature.
 *
 * The entries themselves live in the database and are written by admins in the
 * news central (`/admin/news`, see `admin/NewsManager.jsx`); this module only
 * fetches them and decides how they are presented:
 *
 *   loadNews()            one shared fetch of GET /news per page load
 *   aggregateNews()       collapse repeated events into one dated entry
 *   proteinArrivalEntry() the automatic "N new proteins" entry
 *
 * Aggregation is deliberately client-side. It is presentation — both backends
 * serve the flat list, and an admin editing an entry sees the rows that
 * actually exist rather than a merged view they cannot edit.
 *
 * An event as served by the API:
 *   { id, date: 'YYYY-MM-DD', kind, title, detail, items, group, major }
 * Events merge when they share `group`, or when their text is identical.
 */

/**
 * Roll-out switch for the whole feature: the About section, the banner, the
 * modal, the Help paragraph and the admin card. Off unless the build sets
 * REACT_APP_SHOW_WHATS_NEW to something true-ish (see docs/toggle-whats-new-flag.md).
 */
const whatsNewFlag = String(process.env.REACT_APP_SHOW_WHATS_NEW ?? 'false').toLowerCase();
export const WHATS_NEW_ENABLED = ['1', 'true', 'yes', 'on'].includes(whatsNewFlag);

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Split 'YYYY-MM-DD' without going through Date(), which shifts by timezone. */
function parts(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  return { y, m, d };
}

export function formatDate(iso) {
  const { y, m, d } = parts(iso);
  if (!y || !m || !d) return String(iso);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/**
 * Human label for the set of dates an aggregated entry happened on.
 *   one date      → '21 August 2026'
 *   two dates     → '14 & 21 August 2026'
 *   three or more → '3–21 August 2026'   (an interval)
 * Redundant month/year are dropped when they are shared.
 */
export function formatDateSpan(dates) {
  const sorted = [...new Set(dates)].sort();
  if (sorted.length === 0) return '';
  if (sorted.length === 1) return formatDate(sorted[0]);

  const first = parts(sorted[0]);
  const last = parts(sorted[sorted.length - 1]);
  const sameYear = first.y === last.y;
  const sameMonth = sameYear && first.m === last.m;

  const from = sameMonth
    ? `${first.d}`
    : sameYear
      ? `${first.d} ${MONTHS[first.m - 1]}`
      : formatDate(sorted[0]);
  const to = formatDate(sorted[sorted.length - 1]);

  // Two occurrences are a pair, not a span — an interval would imply the
  // things in between also happened.
  if (sorted.length === 2) return `${from} & ${to}`;
  // Bare day–day reads as one date ("3–20 August"); spell out the dash when
  // either side carries its own month.
  return sameMonth ? `${from}–${to}` : `${from} – ${to}`;
}

const normalise = s =>
  String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!]+$/, '')
    .trim();

/** Events merge when they carry the same `group`, or say exactly the same thing. */
function aggregationKey(ev) {
  if (ev.group) return `group:${ev.group}`;
  return [
    ev.kind || '',
    normalise(ev.title),
    normalise(ev.detail),
    (ev.items || []).map(normalise).join('|'),
  ].join('::');
}

/** Newest first; ties broken by id (newest written first) so order is stable. */
function byDateDesc(a, b) {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return (b.id || 0) - (a.id || 0);
}

/**
 * Collapse repeated events into one entry each.
 *
 * Returns entries newest-first:
 *   { key, id, kind, title, detail, items, major, dates, count, date,
 *     firstDate, dateLabel }
 * where `id` and `date` are the most recent occurrence, `dates` lists every
 * occurrence newest-first, and `dateLabel` is the interval/list label.
 */
export function aggregateNews(events) {
  const byKey = new Map();

  for (const ev of [...(events || [])].sort(byDateDesc)) {
    const key = aggregationKey(ev);
    const seen = byKey.get(key);
    if (!seen) {
      byKey.set(key, {
        key,
        id: ev.id,
        kind: ev.kind || 'feature',
        title: ev.title,
        detail: ev.detail,
        items: ev.items || [],
        ids: [ev.id],
        major: !!ev.major,
        dates: [ev.date],
      });
      continue;
    }
    // Already sorted newest-first, so the first one seen wins the headline
    // fields; later ones only contribute their date and can raise `major`.
    seen.dates.push(ev.date);
    seen.ids.push(ev.id);
    seen.major = seen.major || !!ev.major;
  }

  return [...byKey.values()]
    .map(entry => ({
      ...entry,
      count: entry.dates.length,
      date: entry.dates[0],
      firstDate: entry.dates[entry.dates.length - 1],
      dateLabel: formatDateSpan(entry.dates),
    }))
    .sort(byDateDesc);
}

/** Highest id in the list — the marker "this user has seen everything" is stored as. */
export function latestNewsId(events) {
  return (events || []).reduce((max, ev) => Math.max(max, ev.id || 0), 0) || null;
}

/**
 * The events this user has not been shown yet, aggregated.
 *
 * Newness is by id, not by date: an admin writing up something that happened
 * last month still needs it to reach people who were here yesterday.
 */
export function unseenNews(seenId, events) {
  const fresh = (events || []).filter(ev => (ev.id || 0) > (seenId || 0));
  return aggregateNews(fresh);
}

/**
 * The automatic data event: proteins that entered the Atlas since the user's
 * previous visit. Shaped like an aggregated entry so the same renderer takes
 * it, but it carries no id — it is per-user, never part of the stored history,
 * and so never something to "acknowledge".
 */
export function proteinArrivalEntry(newProteins) {
  if (!newProteins || !newProteins.count) return null;
  const { count, proteins = [], since } = newProteins;
  // The list itself is rendered as links (see WhatsNew.jsx); the count is
  // exact even when the sample the API carried is not the whole set.
  const rest = Math.max(0, count - proteins.length);
  return {
    key: 'protein-arrivals',
    id: null,
    kind: 'data',
    title: `${count} new ${count === 1 ? 'protein' : 'proteins'} in the Atlas`,
    detail: since
      ? `Added to the data since your last visit on ${formatDate(since.slice(0, 10))}.`
      : 'Added to the data recently.',
    items: [],
    proteins,
    overflow: rest,
    major: false,
    dates: [],
    count: 1,
    date: null,
    dateLabel: '',
    isProteinArrival: true,
  };
}

/**
 * One shared GET /news per page load. The banner, the modal and the About page
 * read the same result rather than each firing its own request; nothing on a
 * page changes what it returns, so caching the promise is enough.
 */
let newsPromise = null;

export function loadNews({ force = false } = {}) {
  if (force) newsPromise = null;
  if (!newsPromise) {
    newsPromise = fetch('/api/fastapi/news', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`GET /news: ${r.status}`))))
      .catch(err => {
        // A failed fetch must not wedge the feature for the rest of the session,
        // and must not break the page it sits on: drop the cache, report
        // "nothing new", let the next page load try again.
        newsPromise = null;
        console.warn('Could not load news:', err);
        return { events: [], seen_id: null, new_proteins: { since: null, count: 0, proteins: [] } };
      });
  }
  return newsPromise;
}

/** Acknowledge everything up to `id`. Best-effort: the UI does not wait on it. */
export function markNewsSeen(id) {
  if (!id) return Promise.resolve();
  return fetch('/api/fastapi/news/seen', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  }).catch(err => console.warn('Could not record news as seen:', err));
}
